import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, Brand, Loader } from "@/components/common";
import { Button } from "@/components/Button";

export default function InvitePage() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user === false) {
      // Not logged in — send to auth, remembering the invite to accept afterwards.
      nav(`/?invite=${token}`, { replace: true });
      return;
    }
    if (user) {
      (async () => {
        try {
          const { data } = await api.get(`/connections/invite/${token}`);
          setInvite(data);
          setState("ready");
        } catch (e) {
          setState("invalid");
        }
      })();
    }
  }, [user, loading, token, nav]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/connections/invite/${token}/accept`);
      toast.success("Connected!");
      nav(`/connections/${data.connection_id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || state === "loading") return <div className="min-h-screen bg-[#070A12]"><Loader /></div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#070A12]">
      <div className="w-full max-w-sm iks-fade-up text-center">
        <div className="flex justify-center mb-8"><Brand size={56} /></div>
        {state === "invalid" ? (
          <div className="iks-card p-8">
            <h2 className="font-display font-bold text-xl text-slate-50">Invite unavailable</h2>
            <p className="text-slate-400 text-sm mt-2">This invite link is invalid or has expired.</p>
            <Button className="mt-6" full onClick={() => nav("/connections")}>Go to Connections</Button>
          </div>
        ) : (
          <div className="iks-card p-8">
            <div className="flex justify-center mb-4"><Avatar photo={invite?.inviter?.profile_photo} name={invite?.inviter?.name} size={72} /></div>
            <h2 className="font-display font-bold text-xl text-slate-50">{invite?.inviter?.name} invited you</h2>
            <p className="text-slate-400 text-sm mt-2">Accept to create a private shared space just for the two of you.</p>
            <Button className="mt-6" full loading={busy} onClick={accept} data-testid="accept-invite-btn">
              <UserPlus size={16} /> Accept invitation
            </Button>
            <button onClick={() => nav("/connections")} className="text-slate-500 text-sm mt-4 hover:text-slate-300">Not now</button>
          </div>
        )}
      </div>
    </div>
  );
}
